"use client"

import React, { useState, useEffect } from "react"
import { useLanguage } from "../contexts/LanguageContext"
import { useToast } from "../components/ui/use-toast"
import PageHeader from "../components/ui/dashboard/PageHeader"
import { Plus, Search, Shield, User, Edit, Trash2, Lock } from "lucide-react"
import {
    Tabs, Card, Flex, Text, Button, Badge, Table, Dialog, TextField, Select, Switch, Checkbox
} from "@radix-ui/themes"

interface UserRole {
    _id: string
    username: string
    email: string
    role: "super_admin" | "admin" | "moderator" | "support"
    isActive: boolean
    createdAt: string
    lastLogin?: string
}

interface Role {
    _id: string
    name: string
    permissions: string[]
    description: string
}

const UsersRolesPage: React.FC = () => {
    const { t } = useLanguage()
    const { toast } = useToast()
    const [activeTab, setActiveTab] = useState("users")

    // State
    const [users, setUsers] = useState<UserRole[]>([])
    const [roles, setRoles] = useState<Role[]>([])
    const [loading, setLoading] = useState(true)
    const [showUserDialog, setShowUserDialog] = useState(false)
    const [showRoleDialog, setShowRoleDialog] = useState(false)
    const [selectedUser, setSelectedUser] = useState<UserRole | null>(null)
    const [selectedRole, setSelectedRole] = useState<Role | null>(null)
    const [searchQuery, setSearchQuery] = useState("")

    // Form State
    const [userFormData, setUserFormData] = useState({
        username: "",
        email: "",
        password: "",
        role: "admin" as "super_admin" | "admin" | "moderator" | "support",
        isActive: true
    })

    const [roleFormData, setRoleFormData] = useState({
        name: "",
        description: "",
        permissions: [] as string[]
    })

    const availablePermissions = [
        "users.view", "users.create", "users.edit", "users.delete",
        "restaurants.view", "restaurants.approve", "restaurants.suspend",
        "orders.view", "orders.manage", "orders.refund",
        "captains.view", "captains.approve", "captains.suspend",
        "promotions.view", "promotions.create", "promotions.edit", "promotions.delete",
        "banners.view", "banners.create", "banners.edit", "banners.delete",
        "notifications.send",
        "financials.view", "financials.manage",
        "subscriptions.view", "subscriptions.manage",
        "settings.view", "settings.edit"
    ]

    // Fetch Users
    const fetchUsers = async () => {
        setLoading(true)
        try {
            // Mock data for development
            setUsers([
                {
                    _id: "1",
                    username: "admin",
                    email: "admin@turbo.sd",
                    role: "super_admin",
                    isActive: true,
                    createdAt: "2024-01-01",
                    lastLogin: "2024-01-15"
                },
                {
                    _id: "2",
                    username: "support",
                    email: "support@turbo.sd",
                    role: "support",
                    isActive: true,
                    createdAt: "2024-01-05"
                }
            ])
            setRoles([
                {
                    _id: "1",
                    name: "Super Admin",
                    description: "Full system access",
                    permissions: availablePermissions
                },
                {
                    _id: "2",
                    name: "Support",
                    description: "Customer support access",
                    permissions: ["users.view", "orders.view", "orders.manage"]
                }
            ])
        } catch (error) {
            console.error("Failed to load users", error)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchUsers()
    }, [])

    const handleSaveUser = async () => {
        try {
            if (selectedUser) {
                toast({ title: t("success"), description: "User updated successfully" })
            } else {
                toast({ title: t("success"), description: "User created successfully" })
            }
            setShowUserDialog(false)
            resetUserForm()
            fetchUsers()
        } catch (error) {
            toast({ title: t("error"), description: "Failed to save user", variant: "destructive" })
        }
    }

    const handleDeleteUser = async (id: string) => {
        if (!window.confirm("Are you sure you want to delete this user?")) return
        try {
            toast({ title: t("success"), description: "User deleted successfully" })
            fetchUsers()
        } catch (error) {
            toast({ title: t("error"), description: "Failed to delete user", variant: "destructive" })
        }
    }

    const handleToggleUserStatus = async (id: string, isActive: boolean) => {
        try {
            toast({ title: t("success"), description: `User ${!isActive ? "activated" : "deactivated"}` })
            fetchUsers()
        } catch (error) {
            toast({ title: t("error"), description: "Failed to update status", variant: "destructive" })
        }
    }

    const handleEditUser = (user: UserRole) => {
        setSelectedUser(user)
        setUserFormData({
            username: user.username,
            email: user.email,
            password: "",
            role: user.role,
            isActive: user.isActive
        })
        setShowUserDialog(true)
    }

    const handleAddUser = () => {
        setSelectedUser(null)
        resetUserForm()
        setShowUserDialog(true)
    }

    const resetUserForm = () => {
        setUserFormData({
            username: "",
            email: "",
            password: "",
            role: "admin",
            isActive: true
        })
    }

    const handleSaveRole = async () => {
        try {
            if (selectedRole) {
                toast({ title: t("success"), description: "Role updated successfully" })
            } else {
                toast({ title: t("success"), description: "Role created successfully" })
            }
            setShowRoleDialog(false)
            resetRoleForm()
            fetchUsers()
        } catch (error) {
            toast({ title: t("error"), description: "Failed to save role", variant: "destructive" })
        }
    }

    const handleEditRole = (role: Role) => {
        setSelectedRole(role)
        setRoleFormData({
            name: role.name,
            description: role.description,
            permissions: role.permissions
        })
        setShowRoleDialog(true)
    }

    const handleAddRole = () => {
        setSelectedRole(null)
        resetRoleForm()
        setShowRoleDialog(true)
    }

    const resetRoleForm = () => {
        setRoleFormData({
            name: "",
            description: "",
            permissions: []
        })
    }

    const togglePermission = (permission: string) => {
        setRoleFormData(prev => ({
            ...prev,
            permissions: prev.permissions.includes(permission)
                ? prev.permissions.filter(p => p !== permission)
                : [...prev.permissions, permission]
        }))
    }

    const getRoleBadgeColor = (role: string) => {
        const colors: Record<string, any> = {
            super_admin: "red",
            admin: "blue",
            moderator: "purple",
            support: "green"
        }
        return colors[role] || "gray"
    }

    const filteredUsers = users.filter(user =>
        user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email.toLowerCase().includes(searchQuery.toLowerCase())
    )

    return (
        <div className="space-y-6">
            <PageHeader
                title={t("usersRoles") || "Users & Roles"}
                subtitle={t("manageUsersRolesDesc") || "Manage admin users and role permissions"}
            />

            <Tabs.Root value={activeTab} onValueChange={setActiveTab}>
                <Tabs.List>
                    <Tabs.Trigger value="users">{t("users") || "Users"}</Tabs.Trigger>
                    <Tabs.Trigger value="roles">{t("roles") || "Roles"}</Tabs.Trigger>
                </Tabs.List>

                <div className="mt-4">
                    <Tabs.Content value="users">
                        <Card>
                            <Flex direction="column" gap="4">
                                <Flex justify="between" align="center">
                                    <div className="relative w-64">
                                        <Search className="absolute top-1/2 -translate-y-1/2 left-3 w-4 h-4 text-gray-400" />
                                        <input
                                            placeholder={t("searchUsers") || "Search users..."}
                                            className="w-full py-2 pl-10 pr-4 border rounded-lg border-gray-300 dark:border-gray-600 bg-transparent"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                        />
                                    </div>
                                    <Button onClick={handleAddUser}>
                                        <Plus className="w-4 h-4 mr-2" /> {t("addUser") || "Add User"}
                                    </Button>
                                </Flex>

                                <Table.Root variant="surface">
                                    <Table.Header>
                                        <Table.Row>
                                            <Table.ColumnHeaderCell>{t("username") || "Username"}</Table.ColumnHeaderCell>
                                            <Table.ColumnHeaderCell>{t("email") || "Email"}</Table.ColumnHeaderCell>
                                            <Table.ColumnHeaderCell>{t("role") || "Role"}</Table.ColumnHeaderCell>
                                            <Table.ColumnHeaderCell>{t("lastLogin") || "Last Login"}</Table.ColumnHeaderCell>
                                            <Table.ColumnHeaderCell>{t("status") || "Status"}</Table.ColumnHeaderCell>
                                            <Table.ColumnHeaderCell>{t("actions") || "Actions"}</Table.ColumnHeaderCell>
                                        </Table.Row>
                                    </Table.Header>
                                    <Table.Body>
                                        {filteredUsers.map(user => (
                                            <Table.Row key={user._id}>
                                                <Table.Cell>
                                                    <Flex align="center" gap="2">
                                                        <User className="w-4 h-4 text-gray-400" />
                                                        <Text weight="bold">{user.username}</Text>
                                                    </Flex>
                                                </Table.Cell>
                                                <Table.Cell>
                                                    <Text size="2">{user.email}</Text>
                                                </Table.Cell>
                                                <Table.Cell>
                                                    <Badge color={getRoleBadgeColor(user.role)}>
                                                        {user.role.replace('_', ' ')}
                                                    </Badge>
                                                </Table.Cell>
                                                <Table.Cell>
                                                    <Text size="2">{user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : "Never"}</Text>
                                                </Table.Cell>
                                                <Table.Cell>
                                                    <Badge color={user.isActive ? "green" : "gray"}>
                                                        {user.isActive ? "Active" : "Inactive"}
                                                    </Badge>
                                                </Table.Cell>
                                                <Table.Cell>
                                                    <Flex gap="2">
                                                        <Button size="1" variant="soft" color="blue" onClick={() => handleEditUser(user)}>
                                                            <Edit className="w-3 h-3" />
                                                        </Button>
                                                        <Button size="1" variant="soft" color="red" onClick={() => handleDeleteUser(user._id)}>
                                                            <Trash2 className="w-3 h-3" />
                                                        </Button>
                                                    </Flex>
                                                </Table.Cell>
                                            </Table.Row>
                                        ))}
                                    </Table.Body>
                                </Table.Root>
                            </Flex>
                        </Card>
                    </Tabs.Content>

                    <Tabs.Content value="roles">
                        <Card>
                            <Flex direction="column" gap="4">
                                <Flex justify="end">
                                    <Button onClick={handleAddRole}>
                                        <Plus className="w-4 h-4 mr-2" /> {t("addRole") || "Add Role"}
                                    </Button>
                                </Flex>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {roles.map(role => (
                                        <Card key={role._id} className="hover:shadow-lg transition-shadow">
                                            <Flex direction="column" gap="3" p="4">
                                                <Flex justify="between" align="center">
                                                    <Flex align="center" gap="2">
                                                        <Shield className="w-5 h-5 text-blue-500" />
                                                        <Text weight="bold" size="4">{role.name}</Text>
                                                    </Flex>
                                                    <Button size="1" variant="soft" onClick={() => handleEditRole(role)}>
                                                        <Edit className="w-3 h-3" />
                                                    </Button>
                                                </Flex>
                                                <Text size="2" color="gray">{role.description}</Text>
                                                <div>
                                                    <Text size="1" weight="bold" color="gray">{t("permissions") || "Permissions"}:</Text>
                                                    <div className="flex flex-wrap gap-1 mt-2">
                                                        {role.permissions.slice(0, 5).map(perm => (
                                                            <Badge key={perm} size="1" variant="soft">{perm}</Badge>
                                                        ))}
                                                        {role.permissions.length > 5 && (
                                                            <Badge size="1" variant="soft" color="gray">+{role.permissions.length - 5} more</Badge>
                                                        )}
                                                    </div>
                                                </div>
                                            </Flex>
                                        </Card>
                                    ))}
                                </div>
                            </Flex>
                        </Card>
                    </Tabs.Content>
                </div>
            </Tabs.Root>

            {/* User Dialog */}
            <Dialog.Root open={showUserDialog} onOpenChange={setShowUserDialog}>
                <Dialog.Content style={{ maxWidth: 500 }}>
                    <Dialog.Title>{selectedUser ? (t("editUser") || "Edit User") : (t("addUser") || "Add User")}</Dialog.Title>
                    <Flex direction="column" gap="3">
                        <label>
                            <Text as="div" size="2" mb="1" weight="bold">{t("username") || "Username"}</Text>
                            <TextField.Root value={userFormData.username} onChange={(e) => setUserFormData({ ...userFormData, username: e.target.value })} required />
                        </label>
                        <label>
                            <Text as="div" size="2" mb="1" weight="bold">{t("email") || "Email"}</Text>
                            <TextField.Root type="email" value={userFormData.email} onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })} required />
                        </label>
                        <label>
                            <Text as="div" size="2" mb="1" weight="bold">{t("password") || "Password"} {selectedUser && `(${t("leaveBlank") || "leave blank to keep current"})`}</Text>
                            <TextField.Root type="password" value={userFormData.password} onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })} />
                        </label>
                        <label>
                            <Text as="div" size="2" mb="1" weight="bold">{t("role") || "Role"}</Text>
                            <Select.Root value={userFormData.role} onValueChange={(value: any) => setUserFormData({ ...userFormData, role: value })}>
                                <Select.Trigger style={{ width: "100%" }} />
                                <Select.Content>
                                    <Select.Item value="super_admin">Super Admin</Select.Item>
                                    <Select.Item value="admin">Admin</Select.Item>
                                    <Select.Item value="moderator">Moderator</Select.Item>
                                    <Select.Item value="support">Support</Select.Item>
                                </Select.Content>
                            </Select.Root>
                        </label>
                        <Flex gap="3" mt="4" justify="end">
                            <Dialog.Close>
                                <Button variant="soft" color="gray">{t("cancel") || "Cancel"}</Button>
                            </Dialog.Close>
                            <Button onClick={handleSaveUser}>{t("save") || "Save"}</Button>
                        </Flex>
                    </Flex>
                </Dialog.Content>
            </Dialog.Root>

            {/* Role Dialog */}
            <Dialog.Root open={showRoleDialog} onOpenChange={setShowRoleDialog}>
                <Dialog.Content style={{ maxWidth: 600, maxHeight: "80vh", overflow: "auto" }}>
                    <Dialog.Title>{selectedRole ? (t("editRole") || "Edit Role") : (t("addRole") || "Add Role")}</Dialog.Title>
                    <Flex direction="column" gap="3">
                        <label>
                            <Text as="div" size="2" mb="1" weight="bold">{t("roleName") || "Role Name"}</Text>
                            <TextField.Root value={roleFormData.name} onChange={(e) => setRoleFormData({ ...roleFormData, name: e.target.value })} required />
                        </label>
                        <label>
                            <Text as="div" size="2" mb="1" weight="bold">{t("description") || "Description"}</Text>
                            <TextField.Root value={roleFormData.description} onChange={(e) => setRoleFormData({ ...roleFormData, description: e.target.value })} />
                        </label>
                        <div>
                            <Text as="div" size="2" mb="2" weight="bold">{t("permissions") || "Permissions"}</Text>
                            <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto p-2 border rounded">
                                {availablePermissions.map(perm => (
                                    <label key={perm} className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={roleFormData.permissions.includes(perm)}
                                            onChange={() => togglePermission(perm)}
                                            className="rounded"
                                        />
                                        <Text size="1">{perm}</Text>
                                    </label>
                                ))}
                            </div>
                        </div>
                        <Flex gap="3" mt="4" justify="end">
                            <Dialog.Close>
                                <Button variant="soft" color="gray">{t("cancel") || "Cancel"}</Button>
                            </Dialog.Close>
                            <Button onClick={handleSaveRole}>{t("save") || "Save"}</Button>
                        </Flex>
                    </Flex>
                </Dialog.Content>
            </Dialog.Root>
        </div>
    )
}

export default UsersRolesPage
